import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "../../styles/button.module.scss";
import { CircularProgress, Tooltip } from "@mui/material";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

const ButtonComponent = ({
    ctaText,
    label,
    onClick,
    startIcon,
    endIcon,
    disabled = false,
    variant = "outline",
    className= "",
    loading = false,
    size = "medium",
    menuButton = false,
    menuOptions = [],
    type = "default",
    tooltip = "",
}) => {
    const isIconButton = type === "iconbutton";
    const buttonText = label || ctaText || (isIconButton ? "" : "Click Me");
    const [ripples, setRipples] = useState([]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const menuRef = useRef(null);

    const buttonClass = (() => {
        switch (variant) {
            case "primary":
                // Hyphen vs camelCase depends on css-loader; both avoid silent fallback to unstyled base.
                return styles["button-continer"];
            case "outline":
                return styles["outline-button"];
            case "secondary":
                return styles["secondary-button"];
            case "dotted-outline":
                return styles["dotted-outline-button"];
            default:
                return styles["button-continer"];
        }
    })();

    const sizeClass = size === "small" ? styles["button-small"] : "";

    // Close menu when clicking outside and prevent body scroll
    useEffect(() => {
        if (!menuButton) return;

        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && 
                buttonRef.current && !buttonRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        const handleScroll = (event) => {
            // Prevent scrolling when menu is open
            if (isMenuOpen && !menuRef.current?.contains(event.target)) {
                event.preventDefault();
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isMenuOpen) {
                setIsMenuOpen(false);
            }
        };

        const handleResize = () => {
            if (isMenuOpen) {
                // Small delay to ensure menu has rendered with new dimensions
                setTimeout(() => {
                    const position = calculateMenuPosition();
                    setMenuPosition(position);
                }, 0);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('wheel', handleScroll, { passive: false });
            document.addEventListener('keydown', handleKeyDown);
            window.addEventListener('resize', handleResize);
            // Prevent body scroll
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('wheel', handleScroll);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResize);
            // Restore body scroll
            document.body.style.overflow = '';
        };
    }, [isMenuOpen, menuButton]);

    // Recalculate position after menu renders to get accurate dimensions
    useEffect(() => {
        if (isMenuOpen && menuRef.current) {
            const position = calculateMenuPosition();
            setMenuPosition(position);
        }
    }, [isMenuOpen]);

    const handleClick = (event) => {
        if (disabled || loading) return;
        
        const button = buttonRef.current;
        const rect = button.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = event.clientX - rect.left - size / 2;
        const y = event.clientY - rect.top - size / 2;
        
        const newRipple = {
            x,
            y,
            size,
            id: Date.now()
        };

        setRipples(prev => [...prev, newRipple]);
        
        setTimeout(() => {
            setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
        }, 600);

        if (onClick) {
            onClick(event);
        }
    };

    const calculateMenuPosition = () => {
        if (!buttonRef.current) return { top: 0, left: 0 };
        
        const rect = buttonRef.current.getBoundingClientRect();
        // Use actual menu width if available, otherwise use CSS min-width
        const menuWidth = menuRef.current?.offsetWidth || 200;
        const menuHeight = menuOptions.length * 40; // Approximate height per item
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };
        
        let top = rect.bottom + 4;
        // Align menu's left edge with the main button's left edge for better alignment
        let left = rect.left;
        
        // Adjust horizontal position if menu would overflow on the left
        if (left < 8) {
            // If menu overflows left, align to button's left edge instead
            left = rect.left;
            
            // If still overflows right, keep it within viewport
            if (left + menuWidth > viewport.width - 8) {
                left = viewport.width - menuWidth - 8;
            }
        }
        
        // Ensure menu doesn't overflow on the right
        if (left + menuWidth > viewport.width - 8) {
            left = viewport.width - menuWidth - 8;
        }
        
        // Adjust vertical position if menu would overflow
        if (top + menuHeight > viewport.height - 8) {
            // Show above button instead
            top = rect.top - menuHeight - 4;
            
            // If still doesn't fit, position at top of viewport
            if (top < 8) {
                top = 8;
            }
        }
        
        return { top, left };
    };

    const handleMenuToggle = (event) => {
        event.stopPropagation();
        if (disabled || loading) return;
        
        if (!isMenuOpen && buttonRef.current) {
            const position = calculateMenuPosition();
            setMenuPosition(position);
        }
        
        setIsMenuOpen(!isMenuOpen);
    };

    const handleMenuItemClick = (option) => {
        if (option.onClick) {
            option.onClick();
        }
        setIsMenuOpen(false);
    };

    const combinedClassName = [
        buttonClass,
        sizeClass,
        className,
        styles.ripple,
        loading ? styles.loading : '',
        menuButton ? styles.menuButton : '',
        isIconButton ? styles['icon-button'] : ''
    ].filter(Boolean).join(' ');

    // If it's a menu button, render with menu functionality
    if (menuButton) {
        return (
            <div className={styles.menuButtonWrapper}>
                <button 
                    ref={buttonRef}
                    onClick={handleClick} 
                    className={`${combinedClassName} ${styles.mainButton}`}
                    disabled={disabled || loading}
                >
                    {ripples.map(ripple => (
                        <span
                            key={ripple.id}
                            className={styles.rippleEffect}
                            style={{
                                left: ripple.x,
                                top: ripple.y,
                                width: ripple.size,
                                height: ripple.size,
                            }}
                        />
                    ))}
                    
                    <div className={styles.buttonContent}>
                        <div className={`${styles.contentWrapper} ${loading ? styles.hidden : ''}`}>
                            {startIcon && (
                                <div className={`${styles.iconWrapper} ${disabled ? styles.disabledIcon : ''}`}>
                                    {startIcon}
                                </div>
                            )}
                            {!isIconButton && <span>{buttonText}</span>}
                        </div>
                        
                        {loading && (
                            <div className={styles.loaderWrapper}>
                                <CircularProgress 
                                    size={14} 
                                    thickness={4}
                                    className={styles.loader}
                                />
                            </div>
                        )}
                    </div>
                </button>

                <button
                    className={`${combinedClassName} ${styles.menuToggle}`}
                    onClick={handleMenuToggle}
                    disabled={disabled || loading}
                >
                    <ChevronDownIcon className={`${styles.chevronIcon} ${isMenuOpen ? styles.open : ''}`} />
                </button>

                {isMenuOpen && menuOptions.length > 0 && createPortal(
                    <div 
                        ref={menuRef} 
                        className={styles.menuDropdown}
                        style={{
                            top: menuPosition.top,
                            left: menuPosition.left,
                        }}
                    >
                        {menuOptions.map((option, index) => (
                            <div
                                key={index}
                                className={`${styles.menuItem} ${option.disabled ? styles.disabled : ''}`}
                                onClick={() => !option.disabled && handleMenuItemClick(option)}
                            >
                                {option.icon && (
                                    <span className={styles.menuItemIcon}>
                                        {React.cloneElement(option.icon, { className: 'icon' })}
                                    </span>
                                )}
                                <span className={styles.menuItemText}>{option.label}</span>
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    // Regular button render
    const buttonElement = (
        <button 
            ref={buttonRef}
            onClick={handleClick} 
            className={combinedClassName}
            disabled={disabled || loading}
        >
            {ripples.map(ripple => (
                <span
                    key={ripple.id}
                    className={styles.rippleEffect}
                    style={{
                        left: ripple.x,
                        top: ripple.y,
                        width: ripple.size,
                        height: ripple.size,
                    }}
                />
            ))}
            
            <div className={styles.buttonContent}>
                <div className={`${styles.contentWrapper} ${loading ? styles.hidden : ''}`}>
                    {startIcon && (
                        <div className={`${styles.iconWrapper} ${disabled ? styles.disabledIcon : ''}`}>
                            {startIcon}
                        </div>
                    )}
                    {!isIconButton && <span>{buttonText}</span>}
                    {endIcon && (
                        <div className={`${styles.iconWrapper} ${disabled ? styles.disabledIcon : ''}`}>
                            {endIcon}
                        </div>
                    )}
                </div>
                
                {loading && (
                    <div className={styles.loaderWrapper}>
                        <CircularProgress 
                            size={14} 
                            thickness={4}
                            className={styles.loader}
                        />
                    </div>
                )}
            </div>
        </button>
    );

    // Wrap with Tooltip when tooltip is provided. Use a span wrapper when disabled
    // so the tooltip can show (disabled elements don't receive pointer events).
    if (tooltip) {
        const wrapped = disabled || loading
            ? <span style={{ display: 'inline-block' }}>{buttonElement}</span>
            : buttonElement;
        return (
            <Tooltip title={tooltip} arrow placement="top">
                {wrapped}
            </Tooltip>
        );
    }

    return buttonElement;
};

export default ButtonComponent;